// agentModeView.js — the snapshot, read for the DOM (WS-AGENT-MODE-1).
//
// The shared runtime's `snapshot()` (`@symbo.ls/workspace-model`, README
// "snapshot()") describes the whole screen as plain data: the TUI draws it
// into terminal cells, Agent mode draws it as real DOM. Every frame component
// reads the snapshot through THIS file only, so its shape is known in one
// place. Nothing here decides anything the model decides (selection, filter,
// paging, what a key or a click does): the model already hands renderer-ready
// parts (`content.view`) and the `click` payload of every clickable node,
// which travel as they are. This file picks fields, adds the DOM's row keys
// and maps the model's ink tokens onto brand colour roles.
//
// Rows carry prefixed `key`s: a row keyed by data would otherwise become the
// HTML tag its id names (SYNTAX "A key that equals an HTML tag name BECOMES
// that tag").
//
// Frank note: every export is self-contained (FA204) — small maps restated.

// The model's ink tokens (and status tones) → brand colour roles. Every role
// clears the contrast floor on `document` in both schemes.
export const agentModeInk = (token) => {
  const map = {
    title: 'title',
    strong: 'title',
    text: 'paragraph',
    muted: 'caption',
    faint: 'caption',
    label: 'caption',
    accent: 'accentInk',
    accentBold: 'accentInk',
    success: 'successInk',
    danger: 'dangerInk',
    error: 'dangerInk',
    warning: 'warningInk',
    info: 'caption'
  }
  const t = String(token || '')
  const last = t.includes('+') ? t.slice(t.lastIndexOf('+') + 1) : t
  return map[last] || 'caption'
}

// A status / dot state → its ink (the model's visual vocabulary).
export const agentModeDotInk = (state) =>
  ({ live: 'accentInk', done: 'successInk', warn: 'warningInk', error: 'dangerInk' })[
    String(state || '')
  ] || 'caption'

// The first letter of a person (one capital letter).
export const agentModeInitial = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/[\s._@-]+/)
    .filter(Boolean)
  return parts.length ? parts[0][0].toUpperCase() : '?'
}

// A short stable key for a text (a markdown body re-mounts when it changes).
export const agentModeTextKey = (text) => {
  const str = String(text || '')
  let h = 0
  for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0
  return `t${str.length}-${(h >>> 0).toString(36)}`
}

// An input node of the snapshot ({ target, value, caret, placeholder,
// focused }) as a field row. `extra` adds the DOM's own keys.
export const agentModeFieldRow = (node, key, extra) => {
  const n = node && typeof node === 'object' ? node : {}
  return {
    key,
    target: String(n.target || ''),
    value: String(n.value == null ? '' : n.value),
    caret: Number.isInteger(n.caret) ? n.caret : null,
    placeholder: String(n.placeholder || ''),
    focused: !!n.focused,
    ...(extra || {})
  }
}

// ── frame ──────────────────────────────────────────────────────────────────

export function agentModeHeaderOf(snap) {
  const s = snap && typeof snap === 'object' ? snap : {}
  const status = s.status && typeof s.status === 'object' ? s.status : null
  return {
    host: String(s.host || ''),
    title: String(s.title || ''),
    app: s.app || '',
    tabs: (Array.isArray(s.tabs) ? s.tabs : []).map((t) => ({
      key: `tab-${t.key}`,
      tab: String(t.key),
      label: String(t.label || ''),
      active: !!t.active,
      click: t.click || { tab: t.key }
    })),
    busy: !!(status && status.kind === 'busy'),
    // A toast carries the TUI's mark: ✓ done, ! failed.
    statusText: status
      ? `${status.kind === 'toast' ? { success: '✓ ', error: '! ' }[status.tone] || '' : ''}${String(status.text || '')}`
      : '',
    statusInk: status ? this.call('agentModeInk', status.tone) : 'caption'
  }
}

// The sidebar as rows: the workspace label, group labels, app items.
export const agentModeSidebarRowsOf = (snap) => {
  const side = snap && snap.sidebar && typeof snap.sidebar === 'object' ? snap.sidebar : {}
  const rows = []
  if (side.label) rows.push({ key: 'side-ws', kind: 'label', text: String(side.label) })
  for (const [gi, g] of (Array.isArray(side.groups) ? side.groups : []).entries()) {
    if (g.label) rows.push({ key: `side-g${gi}`, kind: 'label', text: String(g.label), gap: true })
    for (const it of Array.isArray(g.items) ? g.items : []) {
      rows.push({
        key: `side-app-${it.key}`,
        kind: 'item',
        app: String(it.key),
        // The shell's own app glyph (shared glyphNameFor reads a route).
        route: it.key === 'home' ? '/' : `/${it.key}`,
        text: String(it.label || ''),
        hint: String(it.hint || ''),
        number: Number.isInteger(it.index) && it.index < 9 ? String(it.index + 1) : '',
        active: !!it.active,
        cursor: !!side.focused && it.index === side.cursor,
        badge: !!it.badge,
        click: it.click || { app: it.key }
      })
    }
  }
  return rows
}

export function agentModeFooterOf(snap) {
  const f = snap && snap.footer && typeof snap.footer === 'object' ? snap.footer : {}
  return {
    focused: !!f.focused,
    click: f.click || { ask: true },
    field: this.call('agentModeFieldRow', f.input || { target: 'ask' }, 'ask-field', {
      focusPayload: f.click || { ask: true },
      labelKey: 'workspace.agentMode.askAi'
    }),
    hints: (Array.isArray(f.hints) ? f.hints : []).map(([k, v], i) => ({
      key: `hint-${i}`,
      k: String(k),
      v: String(v)
    }))
  }
}

// ── content ────────────────────────────────────────────────────────────────

export const agentModeContentOf = (snap) => {
  const c = snap && snap.content && typeof snap.content === 'object' ? snap.content : null
  if (!c) return { type: 'none', status: 'idle', view: null, data: null, def: {}, label: '' }
  const def = c.def && typeof c.def === 'object' ? c.def : {}
  return {
    type: String(c.type || def.type || 'list'),
    status: String(c.status || 'ready'),
    error: c.error || null,
    label: String(def.label || ''),
    empty: def.empty ? String(def.empty) : '',
    focused: !!c.focused,
    def,
    data: c.data == null ? null : c.data,
    view: c.view && typeof c.view === 'object' ? c.view : null
  }
}

// What the content shows: 'loading', 'error', 'view' or 'none' (README:
// "Show Loading <label>… when status is loading and there is no data").
export function agentModeContentState(snap) {
  const c = this.call('agentModeContentOf', snap)
  if (c.type === 'none') return 'none'
  if (c.status === 'loading' && c.data == null && c.type !== 'assistant') return 'loading'
  if (c.status === 'error' && c.data == null) return 'error'
  return 'view'
}

// A card's / tile's small visual, from the model's spec.
export function agentModeVisualOf(spec) {
  if (!spec || typeof spec !== 'object') return null
  if (spec.kind === 'dot') return { kind: 'dot', ink: this.call('agentModeDotInk', spec.state) }
  if (spec.kind === 'people')
    return {
      kind: 'people',
      people: (spec.names || []).slice(0, 4).map((n, i) => ({
        key: `p-${i}`,
        initial: this.call('agentModeInitial', n),
        name: String(n || '')
      }))
    }
  if (spec.kind === 'progress')
    return { kind: 'progress', value: Math.max(0, Math.min(1, Number(spec.value) || 0)) }
  if (spec.kind === 'spark' || spec.kind === 'ticks') {
    const values = (spec.values || []).map(Number).filter(Number.isFinite).slice(-16)
    const max = Math.max(1, ...values)
    return {
      kind: 'bars',
      ink: spec.state ? this.call('agentModeDotInk', spec.state) : 'caption',
      bars: values.map((v, i) => ({ key: `b-${i}`, share: Math.max(0.08, v / max) }))
    }
  }
  if (spec.kind === 'text')
    return {
      kind: 'text',
      text: String(spec.text || ''),
      ink: this.call('agentModeInk', spec.token || 'muted')
    }
  return null
}

// Board columns (a board, and a dashboard's columns): the cursor marked.
export function agentModeColumnsOf(columns, focused) {
  // Plain loops: every row is plain data (no closure over a column index).
  const out = []
  const cols = Array.isArray(columns) ? columns : []
  for (let ci = 0; ci < cols.length; ci++) {
    const col = cols[ci]
    const cards = []
    const list = Array.isArray(col.cards) ? col.cards : []
    for (let r = 0; r < list.length; r++) {
      const card = list[r]
      cards.push({
        key: `card-${ci}-${r}-${card.id || r}`,
        id: card.id == null ? '' : String(card.id),
        title: String(card.title == null ? '' : card.title),
        right: card.right ? String(card.right) : '',
        rightInk: this.call('agentModeInk', card.rightToken || 'muted'),
        visual: this.call('agentModeVisualOf', card.left),
        selected: !!card.selected,
        focused: !!focused,
        click: card.click || { col: ci, row: r }
      })
    }
    out.push({
      key: `col-${ci}-${col.key}`,
      column: String(col.key),
      label: String(col.label || col.key || ''),
      count: Number(col.count) || 0,
      empty: col.empty ? String(col.empty) : '',
      active: !!col.active,
      focused: !!focused,
      cards
    })
  }
  return out
}

// List: the view-ready rows (after the model's filter), the columns' grid.
export function agentModeListOf(content) {
  const c = content || {}
  const view = c.view || {}
  const defs =
    Array.isArray(view.columns) && view.columns.length
      ? view.columns
      : [{ key: 'title', label: '', flex: 1 }]
  const mainKey = (defs.find((x) => x.flex) || defs[0]).key
  const columns = defs.map((col, i) => ({
    key: `colh-${i}-${col.key}`,
    column: String(col.key),
    label: String(col.label || ''),
    align: col.align === 'right' ? 'right' : 'left',
    track: col.flex ? 'minmax(0, 1fr)' : `${Math.max(2, Number(col.width) || 10)}ch`
  }))
  const rows = Array.isArray(view.rows) ? view.rows : []
  const hasMark = rows.some((r) => r && (r.dot || r.people))
  const template = ['0', ...(hasMark ? ['1ch'] : []), ...columns.map((x) => x.track)].join(' ')
  return {
    columns,
    template,
    hasHead: columns.some((x) => x.label),
    hasMark,
    filter: view.filter
      ? this.call('agentModeFieldRow', view.filter, 'filter-field', {
          labelKey: 'workspace.agentMode.filter'
        })
      : null,
    rows: rows.map((r, i) =>
      r && r.group !== undefined
        ? { key: `lg-${i}`, kind: 'group', text: String(r.group || ''), template }
        : {
            key: `lr-${i}-${(r && r.id) || ''}`,
            kind: 'row',
            id: r && r.id != null ? String(r.id) : '',
            strong: !!(r && r.strong),
            selected: !!(r && r.selected),
            focused: !!c.focused,
            template,
            click: (r && r.click) || { sel: i },
            mark: hasMark
              ? r && r.people && r.people.length
                ? { kind: 'person', text: this.call('agentModeInitial', r.people[0]), ink: 'title' }
                : r && r.dot
                  ? { kind: 'dot', text: '•', ink: this.call('agentModeDotInk', r.dot) }
                  : { kind: 'none', text: '', ink: 'caption' }
              : null,
            cells: columns.map((col, ci) => ({
              key: `cell-${ci}`,
              text: String((r && r.cells && r.cells[col.column]) ?? ''),
              main: col.column === mainKey,
              align: col.align,
              strong: !!(r && r.strong)
            }))
          }
    )
  }
}

// Detail: one record as a document (the record is `content.data`).
export const agentModeDetailOf = (content) => {
  const d = (content && content.data) || {}
  const sections = []
  const list = Array.isArray(d.sections) ? d.sections : []
  for (let si = 0; si < list.length; si++) {
    const s = list[si]
    const items = []
    const raw = Array.isArray(s.items) ? s.items : []
    for (let ii = 0; ii < raw.length; ii++) {
      const it = raw[ii]
      items.push({
        key: `si-${si}-${ii}`,
        author: String(it.author || ''),
        time: it.time ? String(it.time) : '',
        text: String(it.text || '')
      })
    }
    sections.push({
      key: `s-${si}`,
      title: String(s.title || ''),
      empty: s.empty ? String(s.empty) : '',
      items
    })
  }
  return {
    id: d.id == null ? '' : String(d.id),
    title: String(d.title || ''),
    meta: (Array.isArray(d.meta) ? d.meta : []).map((m) => String(m)).join(' · '),
    fields: (Array.isArray(d.fields) ? d.fields : []).map(([label, value], i) => ({
      key: `f-${i}`,
      label: String(label || ''),
      value: String(value == null ? '' : value)
    })),
    body: d.body ? String(d.body) : '',
    sections
  }
}

// Dashboard: greeting, tiles; the columns go through agentModeColumnsOf.
export function agentModeDashboardOf(content) {
  const v = (content && content.view) || {}
  return {
    greeting: String(v.greeting || ''),
    subtitle: String(v.subtitle || ''),
    errors: Array.isArray(v.errors) ? v.errors.length : Number(v.errors) || 0,
    tiles: (Array.isArray(v.tiles) ? v.tiles : []).map((t, i) => ({
      key: `tile-${i}`,
      label: String(t.label || ''),
      value: String(t.value == null ? '' : t.value),
      hint: t.hint ? String(t.hint) : '',
      dot: t.state ? this.call('agentModeDotInk', t.state) : null,
      progress: t.progress != null ? Math.max(0, Math.min(1, Number(t.progress) || 0)) : null
    }))
  }
}

// Chat: channels, the open channel's messages, the composer.
export function agentModeChatOf(content) {
  const v = (content && content.view) || {}
  const channel = v.channel && typeof v.channel === 'object' ? v.channel : null
  return {
    channels: (Array.isArray(v.channels) ? v.channels : []).map((x, i) => ({
      key: `ch-${i}-${x.id}`,
      id: String(x.id),
      label: String(x.label || ''),
      selected: !!x.selected,
      click: x.click || { select: { channel: x.id } }
    })),
    active: channel ? { id: String(channel.id), label: String(channel.label || '') } : null,
    topic: channel && channel.topic ? String(channel.topic) : '',
    messages: (Array.isArray(v.messages) ? v.messages : []).map((m, i) => ({
      key: `msg-${i}-${m.id || ''}`,
      author: String(m.author || ''),
      mine: !!m.mine,
      time: m.time ? String(m.time) : '',
      text: String(m.text || '')
    })),
    writing: !!v.writing,
    writeClick: v.writeClick || { write: true },
    composer: this.call(
      'agentModeFieldRow',
      v.composer || { target: 'composer' },
      'composer-field',
      {
        focusPayload: v.writeClick || { write: true },
        labelKey: 'workspace.agentMode.writeMessage'
      }
    )
  }
}

// Ask AI: the conversation the runtime keeps, and the model's suggestions.
export const agentModeAssistantOf = (snap) => {
  const a = (snap && snap.assistant) || {}
  const v = (snap && snap.content && snap.content.view) || {}
  return {
    streaming: !!a.streaming,
    suggestions: (Array.isArray(v.suggestions) ? v.suggestions : []).map((t, i) => ({
      key: `sug-${i}`,
      text: String(t)
    })),
    turns: (Array.isArray(a.turns) ? a.turns : []).map((t, i) => ({
      key: `turn-${i}`,
      role: t.role === 'user' ? 'user' : 'assistant',
      text: String(t.text || ''),
      status: String(t.status || 'done'),
      error: t.error && t.error.message ? String(t.error.message) : '',
      hint: t.error && t.error.hint ? String(t.error.hint) : ''
    }))
  }
}

// ── overlays ───────────────────────────────────────────────────────────────

// The top overlay (the one that takes the keys), or null.
export function agentModeOverlayOf(snap) {
  const list = snap && Array.isArray(snap.overlays) ? snap.overlays : []
  const o = list[list.length - 1]
  if (!o || typeof o !== 'object') return null
  const kind = String(o.kind || '')
  const title = String(o.title || '')
  if (kind === 'picker') {
    return {
      kind,
      title,
      emptyText: o.emptyText ? String(o.emptyText) : '',
      query: this.call('agentModeFieldRow', o.query || { target: 'query' }, 'picker-query', {
        labelText: title
      }),
      items: (Array.isArray(o.items) ? o.items : []).map((it, i) => ({
        key: `pick-${i}`,
        label: String(it.label || ''),
        hint: it.hint ? String(it.hint) : '',
        ink: it.token ? this.call('agentModeInk', it.token) : 'title',
        selected: !!it.selected,
        enabled: it.enabled !== false,
        click: it.click || { pick: i }
      }))
    }
  }
  if (kind === 'form') {
    return {
      kind,
      title,
      submitLabel: String(o.submitLabel || ''),
      submitFocused: !!o.submitFocused,
      submitClick: o.submitClick || { submit: true },
      fields: (Array.isArray(o.fields) ? o.fields : []).map((f, i) => {
        const type = f.type === 'select' || f.type === 'editor' ? f.type : 'text'
        const click = f.click || { field: i }
        const node =
          type === 'text' && f.input
            ? f.input
            : { target: f.target, value: f.value, focused: f.focused }
        const options = Array.isArray(f.options) ? f.options : []
        return {
          key: `field-${i}-${f.key}`,
          field: String(f.key),
          label: String(f.label || ''),
          type,
          focused: !!f.focused,
          error: f.error ? String(f.error) : '',
          click,
          optionLabel:
            type === 'select' ? String((options[Number(f.choice) || 0] || {}).label || '') : '',
          input: this.call('agentModeFieldRow', node, `form-${type}`, {
            focusPayload: click,
            labelText: String(f.label || '')
          })
        }
      })
    }
  }
  if (kind === 'confirm') {
    return {
      kind,
      title,
      message: String(o.message || ''),
      danger: !!o.danger,
      yes: String(o.yes || ''),
      no: String(o.no || ''),
      choice: Number(o.choice) || 0,
      yesClick: o.yesClick || { ok: true },
      noClick: o.noClick || { ok: false }
    }
  }
  if (kind === 'help') {
    const helpSections = []
    const list = (Array.isArray(o.sections) ? o.sections : []).filter(
      (s) => Array.isArray(s.rows) && s.rows.length
    )
    for (let si = 0; si < list.length; si++) {
      const rows = []
      for (let ri = 0; ri < list[si].rows.length; ri++) {
        const [k, label] = list[si].rows[ri]
        rows.push({ key: `hr-${si}-${ri}`, k: String(k), label: String(label) })
      }
      helpSections.push({ key: `hs-${si}`, title: String(list[si].title || ''), rows })
    }
    return {
      kind,
      title,
      sections: helpSections,
      // The TUI's lines (shell/overlays.js drawHelp): per section its title,
      // its rows and a blank line. `ui` is the model's own object (the
      // renderer's scroll memory, `ui.top` in lines), never a copy.
      lines: helpSections.reduce((n, sec) => n + sec.rows.length + 2, 0),
      ui: o.ui && typeof o.ui === 'object' ? o.ui : null
    }
  }
  return null
}

// ── per-view models from the root (what the view components call) ─────────
// Each reads the snapshot first, so a runtime render re-runs its caller.

// Whether the content shows the view of this type.
export function agentModeViewShown(root, type) {
  const snap = this.call('agentModeSnapshot', root)
  if (this.call('agentModeContentState', snap) !== 'view') return false
  return this.call('agentModeContentOf', snap).type === type
}

export function agentModeBoardModel(root) {
  const c = this.call('agentModeContentOf', this.call('agentModeSnapshot', root))
  return c.type === 'board' || c.type === 'dashboard'
    ? this.call('agentModeColumnsOf', (c.view || {}).columns, c.focused)
    : []
}

export function agentModeListModel(root) {
  const c = this.call('agentModeContentOf', this.call('agentModeSnapshot', root))
  return this.call('agentModeListOf', c.type === 'list' ? c : {})
}

export function agentModeDetailModel(root) {
  const c = this.call('agentModeContentOf', this.call('agentModeSnapshot', root))
  return this.call('agentModeDetailOf', c.type === 'detail' ? c : {})
}

export function agentModeDashboardModel(root) {
  const c = this.call('agentModeContentOf', this.call('agentModeSnapshot', root))
  return this.call('agentModeDashboardOf', c.type === 'dashboard' ? c : {})
}

export function agentModeChatModel(root) {
  const c = this.call('agentModeContentOf', this.call('agentModeSnapshot', root))
  return this.call('agentModeChatOf', c.type === 'chat' ? c : {})
}

export function agentModeAssistantModel(root) {
  return this.call('agentModeAssistantOf', this.call('agentModeSnapshot', root))
}

export function agentModeOverlayModel(root) {
  return this.call('agentModeOverlayOf', this.call('agentModeSnapshot', root))
}

// The selected row or card stays in sight (the TUI scrolls its window to the
// cursor; the DOM scrolls the nearest scroller).
export const agentModeRevealSelected = (el, s) => {
  if (!el || !s || !s.selected) return
  queueMicrotask(() => {
    const node = el.node
    if (node && node.isConnected && typeof node.scrollIntoView === 'function')
      node.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  })
}
